"use client";
import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import CompactSnackbar from "@/components/ui/Snackbar";
import { useStickyHeaderContext } from "./HeaderContext";

export type SnackbarType = "success" | "error" | "info" | "warning";

interface Snackbar {
  id: string;
  message: string;
  type: SnackbarType;
  duration?: number;
  theme?: "light" | "dark";
}

interface SnackbarContextType {
  showSnackbar: (options: Omit<Snackbar, "id">) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const SnackbarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [snackbars, setSnackbars] = useState<Snackbar[]>([]);
  const { headerHeight } = useStickyHeaderContext();
  const showSnackbar = useCallback((options: Omit<Snackbar, "id">) => {
    const id = Math.random().toString(36).substr(2, 9);
    setSnackbars((prev) => [...prev, { ...options, id }]);
  }, []);

  const handleClose = (id: string) => {
    setSnackbars((prev) => prev.filter((snack) => snack.id !== id));
  };
    const topOffset = Math.max(headerHeight + 16, 16);


  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <div className="fixed right-4 flex flex-col gap-2 items-end pointer-events-none"
      style={{ top: `${topOffset}px`, isolation: "isolate" }}  >
        {snackbars.map((snackbar, idx) => (
          <CompactSnackbar
            key={snackbar.id}
            message={snackbar.message}
            type={snackbar.type}
            duration={snackbar.duration}
            theme={snackbar.theme}
            index={idx}
            onClose={() => handleClose(snackbar.id)}
          />
        ))}
      </div>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
}; 